import { apiClient } from './client';
import type {
  FileEntryCondition,
  FormSection,
  WorkflowField,
  WorkflowFieldPropertiesPayload,
  WorkflowFieldType,
} from './workflows';

export type CustomElementType = 'Field' | 'Section' | 'Step';

/** Wire shape for a field saved/edited standalone (no workflow context to place it into yet). */
export type CustomFieldPayload = {
  type: WorkflowFieldType;
  labelEn: string;
  labelFr?: string | null;
  required?: boolean;
  properties?: WorkflowFieldPropertiesPayload;
};

/** Same field shape, plus visibility conditions — only meaningful nested inside a Section/Step payload. */
export type CustomSectionFieldPayload = CustomFieldPayload & {
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
  /** The field's stable id, so the backend can preserve identity across saves instead
   *  of minting a new one — otherwise conditions[].fieldId referencing a sibling field
   *  in the same section/step go stale on every save and get stripped server-side. */
  id?: string;
};

export type CustomSectionColumnPayload = { field: CustomSectionFieldPayload | null };
export type CustomSectionRowPayload = { columns: CustomSectionColumnPayload[] };

export type CustomSectionPayload = {
  titleEn: string;
  titleFr?: string | null;
  rows: CustomSectionRowPayload[];
  conditionCombinator?: 'all' | 'any' | null;
  conditions?: FileEntryCondition[];
};

export type CustomStepContentPayload = {
  stepTitleEn: string;
  stepTitleFr?: string | null;
  stepDescriptionEn?: string | null;
  stepDescriptionFr?: string | null;
  sections: CustomSectionPayload[];
};

export type CustomStepContentResponse = {
  stepTitleEn: string;
  stepTitleFr: string;
  stepDescriptionEn: string;
  stepDescriptionFr: string;
  sections: FormSection[];
};

export type CustomElement = {
  id: string;
  nameEn: string;
  nameFr: string;
  type: CustomElementType;
  author: string;
  createdAt: string;
  updatedAt: string;
  field: WorkflowField | null;
  section: FormSection | null;
  step: CustomStepContentResponse | null;
};

export const customElementsApi = {
  list: async (type?: CustomElementType): Promise<CustomElement[]> => {
    const res = await apiClient.get<CustomElement[]>('/custom-elements', {
      params: type ? { type } : undefined,
    });
    return res.data;
  },

  get: async (id: string): Promise<CustomElement> => {
    const res = await apiClient.get<CustomElement>(`/custom-elements/${id}`);
    return res.data;
  },

  saveField: async (
    nameEn: string,
    nameFr: string | null,
    field: CustomFieldPayload,
  ): Promise<CustomElement> => {
    const res = await apiClient.post<CustomElement>('/custom-elements/fields', {
      nameEn,
      nameFr,
      field,
    });
    return res.data;
  },

  saveSection: async (
    nameEn: string,
    nameFr: string | null,
    section: CustomSectionPayload,
  ): Promise<CustomElement> => {
    const res = await apiClient.post<CustomElement>('/custom-elements/sections', {
      nameEn,
      nameFr,
      section,
    });
    return res.data;
  },

  saveStep: async (
    nameEn: string,
    nameFr: string | null,
    sourceStepId: string,
    step: CustomStepContentPayload,
  ): Promise<CustomElement> => {
    const res = await apiClient.post<CustomElement>('/custom-elements/steps', {
      nameEn,
      nameFr,
      sourceStepId,
      step,
    });
    return res.data;
  },

  updateField: async (
    id: string,
    nameEn: string,
    nameFr: string | null,
    field: Omit<CustomFieldPayload, 'type'>,
  ): Promise<CustomElement> => {
    const res = await apiClient.patch<CustomElement>(`/custom-elements/${id}/field`, {
      nameEn,
      nameFr,
      field,
    });
    return res.data;
  },

  updateSection: async (
    id: string,
    nameEn: string,
    nameFr: string | null,
    section: CustomSectionPayload,
  ): Promise<CustomElement> => {
    const res = await apiClient.patch<CustomElement>(`/custom-elements/${id}/section`, {
      nameEn,
      nameFr,
      section,
    });
    return res.data;
  },

  updateStep: async (
    id: string,
    nameEn: string,
    nameFr: string | null,
    sourceStepId: string,
    step: CustomStepContentPayload,
  ): Promise<CustomElement> => {
    const res = await apiClient.patch<CustomElement>(`/custom-elements/${id}/step`, {
      nameEn,
      nameFr,
      sourceStepId,
      step,
    });
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/custom-elements/${id}`);
  },
};
